// @givmo/board — the minutes lifecycle, and the content address an approval echoes.
//
// ══ THE LIFECYCLE MAPS ONTO THE RECORD CORE WITH NO NEW STATE ══════════════════════════════════
//
//   plan §1.2                        record core
//   ─────────────────────────────    ───────────────────────────────────────────────
//   draft                            `draft`
//   review                           `submitted`
//   approved at the next meeting     `approved`, through one of TWO doors
//   signed                           `signed` EVENTS on the append-only log
//
// **Signing is an EVENT, never a state.** Signing follows approval and an approved record is
// immutable, so what a signature adds is evidence of a human act — which is exactly what an
// append-only log is for. A seat signing twice is refused (409) rather than absorbed, because every
// row in that log is evidence.
//
// ══ ⚠ THIS APP DOES NOT COMPUTE A HASH ANYWHERE, AND MUST NOT START ════════════════════════════
//
// Every board approval echo in this module now comes off the wire, derived on the read by the
// authoritative implementation:
//
//   * minutes — `BoardMinutesOut.content_hash`, `record.content_hash or record.compute_hash()` in
//     `board_minutes_service._minutes_out`;
//   * the record core — `OrgBoardRecordOut.pending_content_hash`, the same `record.compute_hash()` the
//     seal runs, derived in `project_record` on every read of a row that is not yet the record.
//
// This app briefly carried a PORT of the backend's canonical hashing form, because the record core's
// generic approval route needed an echo a draft published nowhere. It is gone: a second copy of
// frozen law in a second language, and one that could not answer for a payload-carrying record at all
// (Python and JavaScript disagree about an integral float, a payload is JSONB whose numbers arrive
// already parsed, and the difference is undetectable and fatal). Anything that re-introduces a
// client-side hash re-introduces a divergence that fails silently — as a 409 saying the record
// changed, on a record that did not.
//
// ⚠ **AND THE SAME-READ BINDING IS THE APPROVAL-INTEGRITY SEMANTIC.** The hash an approval echoes must
// come from THE SAME RESPONSE OBJECT that rendered the content the approver read — never refetched at
// approval time. A refetch would echo the hash of bytes nobody looked at, which turns the echo from a
// proof into a formality: the whole point is that the approval names the exact artifact a human
// approved. {@link approvalEcho} exists to make that binding explicit at the call site rather than
// implicit in a component's variable scope.
//
// ══ THE STALENESS GATE IS A SECOND, DIFFERENT PROTECTION ═══════════════════════════════════════
//
// The hash echo catches the BYTES changing between review and approval. It cannot catch the payload
// SNAPSHOT having fallen behind the motions table, because in that case the bytes never moved. So the
// approval door re-derives the structured record and refuses when it differs. Both refusals arrive as
// 409 with the backend's own sentence, and the UI renders that sentence rather than paraphrasing it —
// the two failures need different actions from the officer (re-read vs. refresh the draft) and the
// backend's message is the one that says which.

import { isGivmoError } from '@givmo/api-client';
import type {
  BoardContemporaneityItemOut,
  BoardContemporaneityOut,
  BoardMinutesOut,
  BoardMinutesSignatureOut,
} from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { humanize } from '../../lib/display';
import { authMethodLabel, signatureActLine } from '../consents/consents-decode';
import { officerRolesL2Words as l2 } from '../roster/officer-roles-l2-words';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
import { recordPersonWords as fix49 } from '../records/record-person-words';

export { displayDate, displayInstant, todayAsDateInput } from '../../lib/display';

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ── The lifecycle ────────────────────────────────────────────────────────────────────────────────

/** Whether these minutes have become the organization's record. */
export function isTheRecord(minutes: BoardMinutesOut | null | undefined): boolean {
  const state = minutes?.state;
  return state === 'approved' || state === 'signed';
}

/** Whether a draft exists at all — the wire carries a record id once one has been opened. */
export function hasDraft(minutes: BoardMinutesOut | null | undefined): boolean {
  return (minutes?.record_id ?? '').length > 0;
}

/**
 * The lifecycle label.
 *
 * ⚠ `submitted` reads as "In review" rather than "Submitted for review" — and NOT as any of the
 * §3.3 collocations the terminology rail bans (`independent review`, `annual review`, `our review`).
 * The bare word is the ordinary sense and is sanctioned; the checking-function sense is what is
 * banned.
 *
 * ⚠ `approved` reads "Approved", whoever approved them (CBG-FIX70). Minutes become the record by one
 * of four ways — the board's motion at a later meeting, written consent, the signatures of the
 * directors who were in a closed session, or one officer alone where the board's rule allows it
 * (CBG-RC11) — and "Approved by the board" was false of the last. The approval line beside the chip
 * names the way, from the read's own facts; the chip states only the standing. Approved (CBG-FIX70)
 * in CBG-YR4-COPY1's next part, 2026-10-16, over the other choice, a chip for each way.
 */
export function minutesStateLabel(state: string | null | undefined): string {
  switch (state) {
    case 'draft':
      return 'Draft';
    case 'submitted':
      return 'In review';
    case 'approved':
      return 'Approved';
    case 'signed':
      return 'Signed';
    default:
      return humanize(state);
  }
}

export function minutesStateTone(state: string | null | undefined): BadgeTone {
  switch (state) {
    case 'approved':
    case 'signed':
      return 'success';
    case 'submitted':
      return 'info';
    case 'draft':
      return 'neutral';
    default:
      return 'neutral';
  }
}

// ── The approval echo ────────────────────────────────────────────────────────────────────────────

/**
 * The content address to echo, taken from the read that rendered what the approver saw.
 *
 * ⚠ **PASS THE SAME OBJECT THE PAGE RENDERED FROM.** This function exists so that binding is a
 * visible argument at the call site instead of an accident of which variable happened to be in scope.
 * Re-reading the minutes to obtain a hash immediately before approving would echo the hash of bytes
 * nobody read — the approval would still succeed, and would mean nothing.
 *
 * `null` when the wire carried no hash, which is the honest signal that there is nothing to approve
 * yet rather than a value to synthesize. The surface withdraws the action and says so.
 */
export function approvalEcho(renderedMinutes: BoardMinutesOut | null | undefined): string | null {
  const hash = (renderedMinutes?.content_hash ?? '').trim();
  return hash.length > 0 ? hash : null;
}

/** A bare calendar date on the wire. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The date these minutes are about — the one their approval seals (CBG-FIX6; D-CBG-8).
 *
 * It is the minutes record's own `occurred_on` (`BoardMinutesOut`), which the draft takes from its
 * meeting's day when it opens, so an approval screen shows the date it seals rather than re-deriving
 * one from the meeting — read from THE SAME OBJECT the page rendered, like the echo above. Three
 * answers, and they are different facts:
 *
 * * a `YYYY-MM-DD` string — the minutes' date;
 * * `null` — the read says the minutes have NO date, so they cannot be approved yet;
 * * `undefined` — the read said nothing a date can be taken from (the field absent, or a value this
 *   build cannot read). That is not "no date", and the surface must not refuse on it: the backend
 *   decides.
 */
export function minutesDate(
  renderedMinutes: BoardMinutesOut | null | undefined,
): string | null | undefined {
  if (renderedMinutes === null || renderedMinutes === undefined) return undefined;
  const raw = renderedMinutes.occurred_on;
  if (raw === null) return null;
  if (typeof raw === 'string' && CALENDAR_DATE.test(raw.trim())) return raw.trim();
  return undefined;
}

/**
 * Whether a failed minutes read means "none have been drafted" rather than "we could not load them".
 *
 * ⚠ These need different screens and an earlier draft gave them the same one. The read 404s while no
 * draft exists — the ordinary state — so a page that treats EVERY error as absence offers "Draft the
 * minutes" after a 500 or a network blip, and (worse) reports the meeting as still open for editing
 * when it may in fact be approved and frozen. That is fail-OPEN on the mutability boundary: the
 * officer is shown write affordances the backend will refuse.
 */
export function isMinutesAbsence(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

// ── Signatures ───────────────────────────────────────────────────────────────────────────────────

export function extractSignatures(
  minutes: BoardMinutesOut | null | undefined,
): BoardMinutesSignatureOut[] {
  return [...(minutes?.signatures ?? [])];
}

/** One signature the minutes await under the board's adopted duties (CBG-YR3-ROLES layer 3). */
export interface AwaitedSignature {
  boardMemberId: string;
  name: string;
  capacityTitleId: string | null;
  capacityLabel: string;
}

/**
 * Whose signature the minutes await — `awaiting_signatures`, served only for the meeting's own
 * approved minutes once the board has adopted duties that give a title "Signs the minutes" (backend
 * `board-officer-roles.md` §14.5): the seats holding such a title whose custody covers the meeting,
 * not yet signed, each with that title. Empty where it is absent or null (no duties adopted, or not
 * the meeting's own minutes), or for a row missing a seat, a name or a title's words.
 */
export function extractAwaitedSignatures(
  minutes: BoardMinutesOut | null | undefined,
): AwaitedSignature[] {
  const rows: unknown = minutes?.awaiting_signatures;
  if (!Array.isArray(rows)) return [];
  const awaited: AwaitedSignature[] = [];
  for (const raw of rows) {
    if (typeof raw !== 'object' || raw === null) continue;
    const row = raw as Record<string, unknown>;
    const seat = typeof row.board_member_id === 'string' ? row.board_member_id : '';
    const name =
      typeof row.board_member_display_name === 'string' ? row.board_member_display_name.trim() : '';
    const label = typeof row.capacity_label === 'string' ? row.capacity_label.trim() : '';
    if (seat.length === 0 || name.length === 0 || label.length === 0) continue;
    awaited.push({
      boardMemberId: seat,
      name,
      capacityTitleId: typeof row.capacity_title_id === 'string' ? row.capacity_title_id : null,
      capacityLabel: label,
    });
  }
  return awaited;
}

/**
 * How a signature on the minutes was made, in the consent log's words (CBG-UX4): "Signed by the
 * director, confirmed with an authenticator app" or "Signature recorded by an officer on their
 * behalf" — composed by the SAME function (`signatureActLine`), so one act reads the same on every
 * Board surface.
 *
 * ⚠ A director's own act names the FACTOR that answered; an officer's on-behalf entry names none,
 * carries the officer's identity, and always shows its required provenance note. That distinction is
 * CBG-RC4's rule and it is the whole evidentiary difference between the two rows: one is a director
 * acting inside their own session, the other is an officer recording that a director acted somewhere
 * else. The minutes record the capture as ONE token (`capture_method`): `manual_on_behalf` is the
 * officer's class, a factor (`totp`, `email_code`) is the director's own session.
 */
export function minutesSignatureLine(signature: BoardMinutesSignatureOut): string {
  const method = (signature.capture_method ?? '').toLowerCase();
  if (method === 'manual_on_behalf') {
    return signatureActLine({ act: 'signed', signatureClass: 'manual_on_behalf' });
  }
  // Their own signature, worded by the factor it answered with (a passkey: CBG-SEC2). Anyone on the
  // roster signs the minutes, director or not (CBG-YR3-ROLES), so the line says where they signed: a
  // consent's log keeps "Signed by the director", which only a director can be.
  if (method === 'passkey_uv' || method === 'totp' || method === 'email_code') {
    const factor = authMethodLabel(method);
    return factor !== null ? `${roles.minutesOwnSession}, ${factor}` : roles.minutesOwnSession;
  }
  return signatureActLine({ act: 'signed', signatureClass: signature.capture_method });
}

/**
 * Who signed, by the seat's roster name, or the words for a name that isn't shown (CBG-UX4; since
 * CBG-FIX49, C13, "Someone whose name isn’t shown", as every record line says it; approved in
 * CBG-RC11-COPY1).
 */
export function minutesSignerName(signature: BoardMinutesSignatureOut): string {
  const name = (signature.board_member_display_name ?? '').trim();
  return name.length > 0 ? name : fix49.unnamedOpening;
}

/**
 * The signature's name line (CBG-YR3-ROLES layer 2, `l2-signatures`): the signer, with the capacity
 * the signature was given in when one was recorded — "Maren Lindqvist, as Secretary". The capacity is
 * the words the event captured AT THE ACT (`capacity_label`), never the title's name today, so a
 * board renaming the title later never rewrites a signature. An answer without the field reads as a
 * signature given with no capacity.
 */
export function minutesSignerLine(signature: BoardMinutesSignatureOut): string {
  const name = minutesSignerName(signature);
  const capacity =
    typeof signature.capacity_label === 'string' ? signature.capacity_label.trim() : '';
  return capacity.length > 0 ? l2.signedAs(name, capacity) : name;
}

/** Whether this row is an officer's on-behalf entry rather than the director's own act. */
export function isOnBehalf(signature: BoardMinutesSignatureOut): boolean {
  return signature.capture_method === 'manual_on_behalf';
}

// ── The contemporaneity clock ────────────────────────────────────────────────────────────────────

/**
 * The clock's own sentence, rendered verbatim.
 *
 * The Form 990 Part VI line-8 instructions describe documentation as contemporaneous when prepared by
 * the LATER of the next meeting of the governing body or 60 days after. The backend does that
 * arithmetic and writes one sentence carrying the rule and the dates it applied it to — this app shows
 * that sentence and does not compose a second one, because a paraphrase of an instruction is a
 * statement about what the instruction requires.
 */
export function clockStatement(clock: BoardContemporaneityOut | null | undefined): string | null {
  const statement = (clock?.statement ?? '').trim();
  return statement.length > 0 ? statement : null;
}

/**
 * The clock's tone.
 *
 * ⚠ States are named for what is true of the RECORD and the DATE — never for a verdict.
 * `past_the_date` says the date has passed; it does NOT say the organization failed anything, and neither
 * does the colour. So the strongest tone available here is `warning`, never `danger`: a red badge on
 * a governance screen reads as a finding, and there is no finding to report.
 */
export function clockTone(state: string | null | undefined): BadgeTone {
  switch (state) {
    case 'recorded':
      return 'success';
    case 'near':
    case 'past_the_date':
      return 'warning';
    case 'approaching':
    case 'open':
      return 'info';
    default:
      return 'neutral';
  }
}

/**
 * The organization-wide clock list, with anything unusable skipped rather than rendered.
 *
 * ⚠ `extractList<BoardContemporaneityItemOut>` is a blind CAST, not a validation — it hands back
 * whatever shape arrived wearing the generated type. `clock` is non-optional on that type, so
 * `entry.clock.statement` reads as safe and is not: one entry missing it throws inside the map and
 * takes the whole meetings hub down, on a panel that is incidental to why an officer opened the page.
 *
 * The repo's tolerant-reader rule is the standard here — decode never throws, and an entry it cannot
 * read is ABSENT rather than a placeholder. An entry with no statement has nothing to render anyway:
 * the sentence IS the content, and the badge alone would be a state with no rule beside it.
 */
export function extractClockEntries(
  entries: readonly BoardContemporaneityItemOut[],
): BoardContemporaneityItemOut[] {
  return entries.filter((entry) => {
    if (typeof entry !== 'object' || entry === null) return false;
    return typeof entry.clock?.statement === 'string' && entry.clock.statement.trim().length > 0;
  });
}

export function clockStateLabel(state: string | null | undefined): string {
  switch (state) {
    case 'recorded':
      return 'Recorded';
    case 'open':
      return 'Open';
    case 'approaching':
      return 'Approaching';
    case 'near':
      return 'Near';
    case 'past_the_date':
      return 'Past the date';
    default:
      return humanize(state);
  }
}
